import React from 'react';
import { Box } from '@mui/material';
import { sub } from 'date-fns';

import { DateRangePickerValue } from 'ui/MuiDateRangePicker';
import { GridDateFilter } from 'lib/grid/GridDateFilter';
import { AutocompleteFormField } from 'lib/form/fields/AutocompleteFormField';
import { MuiForm } from 'lib/Form';
import { useLocationHelpers } from 'components/location/hooks/useLocationHelpers';
import { Option } from 'lib/types';
import { Row } from 'ui/Flex';
import { ReviewsMetricsSection } from 'components/reviews/analytics/ReviewsMetricsSection';
import { ReviewsLineChart } from 'components/reviews/analytics/ReviewsLineChart';
import { ReviewsByScenarioSection } from 'components/reviews/analytics/ReviewsByScenarioSection';
import { ANALYTICS_DATE_RANGE_SHORTCUTS } from 'lib/date-picker/helpers';

export const ReviewsOverview: React.FC = () => {
    const [dateRange, setDateRange] = React.useState<DateRangePickerValue>({
        startDate: sub(new Date(), { months: 3, days: 1 }),
        endDate: sub(new Date(), { days: 1 })
    });

    const { allLocations } = useLocationHelpers();
    const [selectedLocationIds, setSelectedLocationIds] = React.useState<string[]>([]);

    const locationOptions: Option[] = React.useMemo(() => {
        if (allLocations) {
            return allLocations
                .filter(location => location.state === 'ACTIVE')
                .map(location => ({ value: location._id, label: location.title }));
        }
        return [];
    }, [allLocations]);

    const handleLocationChange = React.useCallback((ids: string | string[]) => {
        if (Array.isArray(ids)) {
            setSelectedLocationIds(ids);
        }
    }, []);

    const handleDateRangeChange = React.useCallback((dateRange: DateRangePickerValue) => {
        // after particular user interactions mui date range picker can return only start date
        // we can either ignore such result or set end date as yesterday
        if (!dateRange.endDate) {
            setDateRange({ startDate: dateRange.startDate, endDate: sub(new Date(), { days: 1 }) });
        } else {
            setDateRange(dateRange);
        }
    }, []);

    const handleSubmit = React.useCallback(() => {}, []);

    return (
        <Box width="100%" paddingTop={2} paddingBottom={8}>
            <Row align="space-between">
                <Box minWidth="300px">
                    <MuiForm
                        initialValues={{ locationIds: selectedLocationIds }}
                        onSubmit={handleSubmit}
                        enableReinitialize
                    >
                        {() => (
                            <Box width="400px">
                                <AutocompleteFormField
                                    name="locationIds"
                                    placeholder="All Locations"
                                    multiple
                                    options={locationOptions}
                                    onSelect={handleLocationChange}
                                    size="small"
                                    textFieldVariant="outlined"
                                    limitTags={2}
                                />
                            </Box>
                        )}
                    </MuiForm>
                </Box>
                <Box minWidth="265px">
                    <GridDateFilter
                        value={dateRange}
                        onChange={handleDateRangeChange}
                        shortcuts={ANALYTICS_DATE_RANGE_SHORTCUTS}
                        clearable={false}
                        label=""
                        paper
                        size="small"
                        fullWidth
                        maxRangeMonths={24}
                    />
                </Box>
            </Row>
            <Box>
                <ReviewsMetricsSection dateRange={dateRange} selectedLocationIds={selectedLocationIds} />
                <ReviewsLineChart dateRange={dateRange} selectedLocationIds={selectedLocationIds} />
                <ReviewsByScenarioSection dateRange={dateRange} selectedLocationIds={selectedLocationIds} />
            </Box>
        </Box>
    );
};
